import { FC, useCallback } from "react";
import {
  useLocation,
  useMatch,
  useNavigate,
  useResolvedPath,
} from "react-router-dom";
import { SvgIconProps } from "@mui/material";
import { SvgIconComponent } from "@mui/icons-material";
import { ProviderType } from "../../../../../services/cloudchipr.api";
import { noOrgAccountOrgId } from "../../../../pages/accounts-group/utils/helpers/noOrganizationConstants";
import { NestedNavItem } from "../../app-nav-components/nested-nav-item/NestedNavItem";
import { useAppDispatch } from "../../../../../store/hooks";
import { useAccountStorageMemoizedTabHook } from "../../../../pages/accounts-group/utils/hooks/useAccountStorageMemoizedTabHook";
import { setAccountsProviderOrganisationId } from "../../../../../store/accounts/accountsSlice";
import { setLiveUsageMgmtProvider } from "../../../../../store/live-usage-mgmt/liveUsageMgmtSlice";
import { useAppNavOverlayContext } from "../../../utils/useAppNavOverlay.context";
import { generateLiveResourcesPath } from "../../../../pages/accounts-group/utils/helpers/generateLiveResourcesPath";
import { ProviderIcon } from "../../../../common/ProviderIcon";

interface LiveUsageNavigationItemProps {
  provider: ProviderType;
  org: string;
  orgId: string;
  accountIds: string[];
  selectedProvider?: ProviderType | null;
  selectedGroupId?: string | null;
}

export const LiveUsageNavigationItem: FC<LiveUsageNavigationItemProps> = ({
  provider,
  accountIds,
  org,
  orgId,
  selectedProvider,
  selectedGroupId,
}) => {
  const navigate = useNavigate();
  const { onClose } = useAppNavOverlayContext();

  const dispatch = useAppDispatch();

  const { getAccountTab } = useAccountStorageMemoizedTabHook();

  const location = useLocation();

  const accountIdsAsString = accountIds?.join(",") ?? "";
  const activePath = location.pathname;
  const accountsPath = generateLiveResourcesPath({ provider, orgId });
  const liveUsagePath = generateLiveResourcesPath({
    provider,
    activeTab: "live-resources",
    accountIds: accountIdsAsString,
  });

  const accountTab = getAccountTab();
  const path = accountTab === "live-resources" ? liveUsagePath : accountsPath;

  const resolvedPath = useResolvedPath(accountsPath || "");
  const match = useMatch({ path: resolvedPath.pathname });
  const accountsOrLiveUsageActive =
    accountsPath.includes(activePath) || liveUsagePath.includes(activePath);
  const accountsPageActive = accountsPath.includes(
    `/${selectedProvider ?? ""}/accounts/${selectedGroupId ?? noOrgAccountOrgId}`,
  );

  const selected =
    (resolvedPath.pathname !== "/" && !!match) ||
    (accountsPageActive && accountsOrLiveUsageActive);

  const setAccountsProviderAndOrganizationId = useCallback(() => {
    dispatch(setAccountsProviderOrganisationId(orgId));
    dispatch(setLiveUsageMgmtProvider(provider));
  }, [dispatch, orgId, provider]);

  const navigateToPath = useCallback(() => {
    navigate(!accountIdsAsString ? accountsPath : path);
    onClose?.();
  }, [accountsPath, path, navigate, onClose, accountIdsAsString]);

  const handleClick = useCallback(() => {
    navigateToPath();
    setAccountsProviderAndOrganizationId();
  }, [setAccountsProviderAndOrganizationId, navigateToPath]);

  const Icon = useCallback(
    (props: SvgIconProps) => <ProviderIcon provider={provider} {...props} />,
    [provider],
  );

  return (
    <NestedNavItem
      nested
      onButtonClick={handleClick}
      key={path}
      primary={org}
      Icon={Icon as SvgIconComponent}
      selected={selected}
    />
  );
};
